import React, { useMemo, useRef } from 'react';
import useMeasure from 'react-use/lib/useMeasure';
import { Wrapper, WrapperProps } from '../Wrapper/Wrapper';
import { Option, Selection, StyledSegmentedControl, Value } from './SegmentedControl.styles';

export interface Props extends Omit<WrapperProps, 'onChange'> {
  id: string;
  values: string[];
  selectedIndex: number;
  ariaLabel?: string;
  onChange(index: number): void;
  renderLabel?(value: string, index: number): React.ReactNode;
}

export const SegmentedControl: React.FC<Props> = ({
  id,
  values,
  selectedIndex,
  onChange,
  ariaLabel,
  renderLabel,
  ...rest
}) => {
  const [ref, { width }] = useMeasure<HTMLDivElement>();

  const canAnimate = useRef(false);

  const tabWidth = useMemo(() => {
    if (width && values && values.length > 0) {
      return width / values.length;
    }

    return 0;
  }, [values, width]);

  return (
    <Wrapper {...rest}>
      <StyledSegmentedControl ref={ref} role="radiogroup" aria-label={ariaLabel}>
        <Selection
          css={`
            width: ${tabWidth}px;
            transform: translateX(${selectedIndex * tabWidth}px);
            transition: ${canAnimate.current ? 'transform .2s ease' : 'none'};
          `}
        />

        {values.map((value, index) => {
          const optionId = `${id}-${value}`;
          const isSelected = selectedIndex === index;

          return (
            <Option
              key={optionId}
              css={{
                '&:not(:first-of-type)::before': {
                  opacity: isSelected ? 0 : 1,
                },
              }}
            >
              <input
                type="radio"
                id={optionId}
                name={id}
                value={value}
                checked={isSelected}
                onChange={() => {
                  canAnimate.current = true;
                  onChange(index);
                }}
              />
              <label htmlFor={optionId}>
                <Value isSelected={isSelected} width={tabWidth - 24}>
                  {renderLabel ? renderLabel(value, index) : value}
                </Value>
              </label>
            </Option>
          );
        })}
      </StyledSegmentedControl>
    </Wrapper>
  );
};
